class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.renderRevision = 0;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
  }

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay || overlay.dataset.cartDrawerBound === 'true') return;
    overlay.dataset.cartDrawerBound = 'true';
    overlay.addEventListener('click', () => this.close());
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (!this.isConnected) return;
    clearTimeout(this.openTimer);
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // Open and focus together, including when reduced motion suppresses transitionend.
    this.openTimer = setTimeout(() => {
      this.openTimer = null;
      if (!this.isConnected) return;
      this.classList.add('animate', 'active');
      const container = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : this.querySelector('#CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      if (this.classList.contains('active') && container && focusElement) trapFocus(container, focusElement);
    });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    clearTimeout(this.openTimer);
    this.openTimer = null;
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  disconnectedCallback() {
    const wasOpening = this.openTimer != null;
    clearTimeout(this.openTimer);
    this.refreshController?.abort();
    if (wasOpening || this.classList.contains('active')) this.close();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  beginUpdate() {
    this.refreshController?.abort();
    return ++this.renderRevision;
  }

  async refreshContents() {
    const revision = this.beginUpdate();
    this.refreshController = new AbortController();
    const url = new URL(routes.cart_url, window.location.origin);
    url.searchParams.set('sections', this.getSectionsToRender().map((section) => section.id).join(','));
    try {
      const response = await fetch(url, {
        cache: 'no-store',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        signal: this.refreshController.signal,
      });
      if (!response.ok) throw new Error(`Cart refresh failed with HTTP ${response.status}`);
      const sections = await response.json();
      if (revision !== this.renderRevision || !this.isConnected) return;
      this.renderContents({ sections }, { open: false });
    } catch (error) {
      if (error.name !== 'AbortError') throw error;
    }
  }

  renderContents(parsedState, { open = true } = {}) {
    if (!parsedState || !parsedState.sections) {
      throw new Error('Cart drawer render received no sections.');
    }

    let isEmpty;
    const updates = this.getSectionsToRender().map((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const sectionHTML = parsedState.sections[section.id];

      if (!sectionElement || typeof sectionHTML !== 'string' || !sectionHTML.trim()) {
        throw new Error(`Cart drawer section ${section.id} is missing.`);
      }

      const sourceElement = this.getSectionDOM(sectionHTML, section.selector);
      if (!sourceElement) throw new Error(`Cart drawer section ${section.id} is invalid.`);

      if (
        section.id === 'cart-drawer' &&
        (!sourceElement.querySelector('.drawer__inner') ||
          !sourceElement.querySelector('.drawer__header') ||
          !sourceElement.querySelector('#CartDrawer-Overlay') ||
          !sourceElement.querySelector('cart-drawer-items') ||
          !sourceElement.querySelector('.cart-drawer__footer'))
      ) {
        throw new Error('Cart drawer response is incomplete.');
      }

      if (section.id === 'cart-drawer') {
        const sourceItems = sourceElement.querySelector('cart-drawer-items');
        isEmpty = sourceItems.classList.contains('is-empty');
        if (!isEmpty && !sourceItems.querySelector('.cart-item')) {
          throw new Error('Cart drawer response is missing its products.');
        }
      }

      return { target: sectionElement, html: sourceElement.innerHTML };
    });

    // Commit the complete validated snapshot together; never mix an old empty panel with new totals.
    this.beginUpdate();
    updates.forEach(({ target, html }) => {
      target.innerHTML = html;
    });

    const drawerInner = this.querySelector('.drawer__inner');
    if (!drawerInner) throw new Error('Cart drawer failed to render its inner panel.');
    this.classList.toggle('is-empty', isEmpty);
    const cartItems = this.querySelector('cart-drawer-items');
    if (cartItems) cartItems.scrollTop = 0;
    this.scrollTop = 0;
    drawerInner.scrollTop = 0;
    this.productId = parsedState.id;
    this.bindOverlay();
    if (open) this.open();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const section = this.getSectionDOM(html, selector);
    if (!section) throw new Error(`Cart section ${selector} was not found.`);
    return section.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    if (typeof html !== 'string' || !html.trim()) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
